import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { useEffect, useState } from 'react';
import { TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useApi } from '@backstage/core-plugin-api';
import { parseEntityRef } from '@backstage/catalog-model';
import { CatalogFilterBlueprint } from '@backstage/plugin-catalog-react/alpha';
import {
  DefaultEntityFilters,
  catalogApiRef,
  useEntityList,
} from '@backstage/plugin-catalog-react';

export class SystemFilter {
  constructor(public readonly values: string[]) {}
  getCatalogFilters() {
    return { 'relations.partOf': this.values };
  }
  toQueryValue() {
    return this.values;
  }
}

type Filters = DefaultEntityFilters & { system?: SystemFilter };

export function SystemCatalogPicker() {
  const catalog = useApi(catalogApiRef);
  const { filters, updateFilters, queryParameters } = useEntityList<Filters>();
  const [options, setOptions] = useState<string[]>([]);
  const [error, setError] = useState(false);
  const kind = filters.kind?.value || 'component';
  const query = JSON.stringify(queryParameters.system || []);
  useEffect(() => {
    const refs = [JSON.parse(query)]
      .flat()
      .filter((ref: string) => ref.startsWith('system:'));
    updateFilters({ system: refs.length ? new SystemFilter(refs) : undefined });
  }, [query, updateFilters]);
  useEffect(() => {
    let active = true;
    setError(false);
    catalog
      .getEntityFacets({ facets: ['relations.partOf'], filter: { kind } })
      .then(
        result => {
          if (active)
            setOptions(
              result.facets['relations.partOf']
                .map(item => item.value)
                .filter(ref => ref.startsWith('system:'))
                .sort(),
            );
        },
        () => {
          if (active) setError(true);
        },
      );
    return () => {
      active = false;
    };
  }, [catalog, kind]);
  return (
    <Autocomplete
      multiple
      options={options}
      value={filters.system?.values || []}
      getOptionLabel={ref => {
        const entity = parseEntityRef(ref);
        return `${entity.name} (${entity.namespace})`;
      }}
      onChange={(_, values) =>
        updateFilters({
          system: values.length ? new SystemFilter(values) : undefined,
        })
      }
      renderInput={params => (
        <TextField
          {...params}
          label="System"
          variant="outlined"
          margin="normal"
          error={error}
          helperText={error ? 'Could not load Systems' : undefined}
        />
      )}
    />
  );
}

export const catalogFiltersModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    CatalogFilterBlueprint.make({
      name: 'system',
      params: {
        loader: async () => <SystemCatalogPicker />,
      },
    }),
  ],
});
